import React from "react";

import Icon from "@common/components/Icon/Icon";
import Tooltip from "@common/components/Tooltip/Tooltip";
import { CrashEvent } from "@common/models/crashReporting";
import identifiers from "@common/util/identifiers.json";
import { RoutePaths } from "@main/router/config/routePaths";
import { generatePath, NavigateFunction } from "react-router-dom";

import * as Styled from "./CrashEventPageNavigationContent.styles";

enum EventType {
  PREVIOUS = "previous",
  NEXT = "next",
}

interface Props {
  crashEvent?: CrashEvent;
  crashReportKey?: string;
  selectedWorkspaceSlug?: string;
  selectedAppKey?: string;
  navigate: NavigateFunction;
}

export default function CrashEventPageNavigationContent({
  crashEvent,
  crashReportKey,
  selectedWorkspaceSlug,
  selectedAppKey,
  navigate,
}: Props) {
  if (!selectedWorkspaceSlug || !selectedAppKey || !crashEvent) {
    return <></>;
  }

  const commonParamValues = {
    workspaceSlug: selectedWorkspaceSlug,
    appKey: selectedAppKey,
    crashReportKey: crashReportKey,
  };

  const allEventsPath = generatePath(RoutePaths.CRASH_REPORT_EVENT, {
    workspaceSlug: commonParamValues.workspaceSlug,
    appKey: commonParamValues.appKey,
    crashReportKey: commonParamValues.crashReportKey ?? null,
    crashReportEventId: crashEvent.id,
  });
  const crashOverviewPath = generatePath(RoutePaths.CRASH_REPORT_OVERVIEW, {
    workspaceSlug: commonParamValues.workspaceSlug,
    appKey: commonParamValues.appKey,
    crashReportKey: commonParamValues.crashReportKey ?? null,
  });

  const getCrashReportEventPath = (eventId: string) => {
    return generatePath(RoutePaths.CRASH_REPORT_EVENT, {
      workspaceSlug: selectedWorkspaceSlug,
      appKey: selectedAppKey,
      crashReportKey: crashReportKey ?? null,
      crashReportEventId: eventId,
    });
  };

  const isOverviewClicked = window.location.pathname.includes("overview");

  return (
    <Styled.CrashNavigation>
      <Styled.CrashNavigationLinks
        $active={isOverviewClicked}
        onClick={() => navigate(crashOverviewPath)}
        data-testid={identifiers["navbar.crashReport.overview"]}
      >
        {"Overview"}
      </Styled.CrashNavigationLinks>
      <Styled.CrashNavigationLinks
        $active={!isOverviewClicked}
        onClick={() => navigate(allEventsPath)}
        data-testid={identifiers["navbar.crashReport.allEvents"]}
      >
        {"All events"}
      </Styled.CrashNavigationLinks>

      <CrashEventsNavigationButton
        type={EventType.PREVIOUS}
        onClick={() => navigate(getCrashReportEventPath(crashEvent?.previous_event ?? ""))}
        disabled={!Boolean(crashEvent.previous_event)}
      />
      <CrashEventsNavigationButton
        type={EventType.NEXT}
        onClick={() => navigate(getCrashReportEventPath(crashEvent?.next_event ?? ""))}
        disabled={!Boolean(crashEvent.next_event)}
      />
    </Styled.CrashNavigation>
  );
}

interface CrashEventNavigationButtonProps {
  style?: React.CSSProperties;
  type: EventType;
  onClick: () => void;
  disabled: boolean;
}

const CrashEventsNavigationButton = ({ style, type, onClick, disabled }: CrashEventNavigationButtonProps) => {
  const text = type === EventType.PREVIOUS ? "Previous event" : "Next event";
  const icon = type === EventType.PREVIOUS ? "caret-left" : "caret-right";

  return (
    <Styled.CrashEventNavigationButton
      style={style}
      onClick={onClick}
      disabled={disabled}
    >
      <Tooltip
        position="bottom"
        text={text}
        disabled={disabled}
      >
        <Icon
          icon={icon}
          size={10}
        />
      </Tooltip>
    </Styled.CrashEventNavigationButton>
  );
};
